import jsPDF from 'jspdf';
import i18n from '../../../i18n';
import { SceneWithUserNames } from '../../../types/scene.types';
import { BreakdownInfo } from '../../../pages/Home/types';
import { setFont, registerFont, SupportedLanguage } from '../PDFExporter';
import { formatDayNightForExport, formatInteriorExteriorForExport } from '../exportSceneFieldFormat';
import { ElementInfo } from '../../../types/element.types';
import { ElementType } from '../../../types/element.types';
import { ElementDataCache, ElementRelationDataCache } from '../../../types/cache.types';
import { SceneCodeParts } from '../../../pages/Breakdown/BreakdownMain/Utils/SceneCodeUtils';
import { sortCastItemsByNO } from '../../../utils/elementSortUtils';
import {
  PdfScenesExportSort,
  sortScenesForPdfExport,
  sortSetsGroupedLocations,
  DEFAULT_PDF_SCENES_EXPORT_SORT
} from '../exportSceneSort';

const t = (key: string, opts?: { lng?: string }) => i18n.t(key, opts);

// 字体文件映射
const FONT_FILES = {
  'zh-CN': {
    name: 'AlibabaPuHuiTi'
  },
  'zh-HK': {
    name: 'AlibabaPuHuiTi'
  },
  'en': {
    name: 'AlibabaPuHuiTi'
  }
};

// 由于 jspdf-autotable 的类型问题，我们使用 require 动态导入
// @ts-ignore
const autoTable = require('jspdf-autotable').default;

// 颜色常量（十六进制格式）
const COLORS = {
  BLUE: '#5349AD',        // RGB: [83, 73, 173] - 蓝色背景
  WHITE: '#FFFFFF',       // RGB: [255, 255, 255] - 白色背景
  LIGHT_GRAY: '#F5F5F5',  // RGB: [245, 245, 245] - 浅灰色背景
  TEXT_BLACK: 0           // 黑色文字（灰度值）
};

// 统一样式常量
const STYLES = {
  HEADER_FONT_SIZE: 16,        // header 字体大小
  HEADER_FONT_STYLE: 'normal', // header 字体样式
  TABLE_FONT_SIZE: 21,         // 表格字体大小
  CELL_PADDING: 2,             // 单元格内边距（缩小50%）
  PAGE_MARGIN: 9,              // 页面边距（缩小40%）
  ROW_HEIGHT: 17,              // 固定行高（两行文本高度）
  HEADER_Y: 12,                // header Y位置
  TABLE_START_Y: 19            // 表格起始Y位置（每页统一）
};

export const PDF_SCENES_SETS_A3_MARGINS_MM = {
  left: STYLES.PAGE_MARGIN,
  right: STYLES.PAGE_MARGIN,
  top: STYLES.TABLE_START_Y,
  bottom: STYLES.PAGE_MARGIN
} as const;

// 将十六进制颜色转换为 RGB 数组
const hexToRgb = (hex: string): number[] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

// 字段映射 - 从全局 i18n 获取
const getFieldMapForLanguage = (language: SupportedLanguage) => ({
  sceneCode: t('export.sceneCode', { lng: language }),
  interiorExterior: t('export.interiorExterior', { lng: language }),
  dayNight: t('export.dayNight', { lng: language }),
  sceneSet: t('export.sceneSet', { lng: language }),
  scenePlot: t('export.scenePlot', { lng: language }),
  sceneLocation: t('export.sceneLocation', { lng: language }),
  cast: t('export.cast', { lng: language })
});

// 公共配置：A3页面初始化
const initializeA3Page = async (
  doc: jsPDF,
  language: SupportedLanguage
): Promise<{ doc: jsPDF; fontRegistered: boolean; fontName: string }> => {
  // 设置页面为横向A3
  doc = new jsPDF('l', 'mm', 'a3');
  
  // 重新注册字体（因为创建了新实例）
  const fontRegistered = await registerFont(doc, language);
  if (!fontRegistered) {
    console.warn(`字体注册失败，将使用默认字体。语言: ${language}`);
  }
  
  // 设置字体
  setFont(doc, language);
  
  // 获取字体名称
  const fontName = fontRegistered ? FONT_FILES[language].name : 'helvetica';
  
  return { doc, fontRegistered, fontName };
};

// 检查场景的所有字段是否都为空
const isSceneRowEmpty = (scene: SceneWithUserNames): boolean => {
  // 检查所有导出字段是否都为空
  const hasSceneCode = !!scene.sceneCode?.trim();
  const hasInteriorExterior = !!scene.sceneInteriorExterior;
  const hasDayNight = !!scene.sceneDayNight;
  const hasSceneSet = !!scene.sceneSet?.trim();
  const hasScenePlotDay = !!scene.scenePlotDay?.trim();
  const hasScenePagesNumber = !!scene.scenePagesNumber;
  const hasScenePlot = !!scene.scenePlot?.trim();
  const hasSceneLocation = !!scene.sceneLocation?.trim();
  const hasScenePeriod = !!scene.scenePeriod?.trim();
  
  // 如果所有字段都为空，则返回true（表示该行应该被过滤）
  return !hasSceneCode && 
         !hasInteriorExterior && 
         !hasDayNight && 
         !hasSceneSet && 
         !hasScenePlotDay && 
         !hasScenePagesNumber && 
         !hasScenePlot && 
         !hasSceneLocation && 
         !hasScenePeriod;
};

// 公共配置：添加标题信息
const addTitleHeader = (
  doc: jsPDF,
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage,
  optionName?: string,
  pageNumber?: number
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const startX = STYLES.PAGE_MARGIN; // 使用统一样式常量
  const titleY = STYLES.HEADER_Y; // 使用统一样式常量
  
  // 从 localStorage 获取 FilmContext 存储的影片名称（filmName）
  const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
  
  // 统一使用 header 字体样式
  doc.setFontSize(STYLES.HEADER_FONT_SIZE);
  setFont(doc, language, 'normal'); // 使用字面量类型以满足 setFont 的类型要求
  doc.setTextColor(0, 0, 0);

  // 左侧：影片名称 | 分场名称
  const versionLabel = t('export.versionLabel', { lng: language });
  const leftTitleText = `《${filmName || ''}》      ${optionName || ''}              ${versionLabel}${breakdownInfo.breakdownName || ''}`;
  doc.text(leftTitleText, startX, titleY);
  
  // 右侧：剧本版本：XXX | 第 X 页
  const versionText = t('export.scriptVersion', { lng: language });
  const pagePrefix = t('export.pagePrefix', { lng: language });
  const pageSuffix = t('export.pageSuffix', { lng: language });
  
  const currentPage = pageNumber !== undefined ? pageNumber : doc.internal.getCurrentPageInfo().pageNumber;
  const pageInfo = pageSuffix ? `${pagePrefix} ${currentPage} ${pageSuffix}` : `${pagePrefix} ${currentPage}`;
  
  const labelSeparator = t('common.labelSeparator', { lng: language }) as string;
  const pipeSeparator = t('common.pipeSeparator', { lng: language }) as string;
  const rightTitleText = `${versionText}${labelSeparator}${breakdownInfo.breakdownScriptOverview || ''}${pipeSeparator}${pageInfo}`;
  
  // 计算右侧文本的X位置（右对齐）
  const rightTextWidth = doc.getTextWidth(rightTitleText);
  const rightX = pageWidth - rightTextWidth - startX;
  doc.text(rightTitleText, rightX, titleY);
};

// 公共配置：获取各场次主要角色列文案（从缓存）
const getCastTextByScenes = (
  scenes: SceneWithUserNames[],
  elementsBySceneId: Map<string, ElementInfo[]>
): Map<string, string> => {
  const getCastColumnTextForScene = (sceneId: string): string => {
    const elements = elementsBySceneId.get(sceneId) || [];
    
    if (!Array.isArray(elements) || elements.length === 0) {
      return '';
    }
    
    const castItems = elements.filter(
      (item: ElementInfo) => item.elementType === ElementType.CAST
    );
    
    const sortedCastItems = sortCastItemsByNO(castItems);
    
    const roleNames = sortedCastItems
      .map((item: ElementInfo) => item.elementAbbr || item.elementName || '')
      .filter(Boolean);
    
    return roleNames.join('/') || '';
  };
  
  const castTextBySceneId = new Map<string, string>();
  scenes.forEach((scene) => {
    const text = getCastColumnTextForScene(scene.sceneID);
    castTextBySceneId.set(scene.sceneID, text);
  });
  
  return castTextBySceneId;
};

// 公共配置：获取表格样式配置
const getTableConfig = (
  doc: jsPDF,
  fontName: string,
  language: SupportedLanguage
) => {
  const pageWidthForTable = doc.internal.pageSize.getWidth();
  const margin = STYLES.PAGE_MARGIN; // 使用统一样式常量
  const tableWidth = pageWidthForTable - (margin * 2);
  
  const columnWidths = {
    0: tableWidth * 0.10,  // 场号
    1: tableWidth * 0.05,  // 内外
    2: tableWidth * 0.05,  // 日夜
    3: tableWidth * 0.18,  // 场景
    4: tableWidth * 0.30,  // 剧情
    5: tableWidth * 0.18,  // 拍摄地点
    6: tableWidth * 0.14   // 主要角色
  };
  
  const fieldMap = getFieldMapForLanguage(language);
  const headers = [
    fieldMap.sceneCode,
    fieldMap.interiorExterior,
    fieldMap.dayNight,
    fieldMap.sceneSet,
    fieldMap.scenePlot,
    fieldMap.sceneLocation,
    fieldMap.cast
  ];
  
  return {
    headers,
    margin,
    tableWidth,
    columnWidths,
    baseStyles: {
      font: fontName,
      fontSize: STYLES.TABLE_FONT_SIZE, // 使用统一样式常量
      cellPadding: STYLES.CELL_PADDING, // 使用统一样式常量
      minCellHeight: STYLES.ROW_HEIGHT, // 设置最小单元格高度
      valign: 'top', // 垂直向上对齐
      textColor: COLORS.TEXT_BLACK, // 正文纯黑，纸质打印更清晰
      lineColor: [0, 0, 0],
      lineWidth: 0.2
    },
    headStyles: {
      fillColor: hexToRgb(COLORS.BLUE),
      textColor: COLORS.TEXT_BLACK, // 黑字 + 原蓝底，便于纸质打印辨认
      fontStyle: 'bold',
      font: fontName,
      valign: 'top', // 垂直向上对齐
      lineColor: [0, 0, 0],
      lineWidth: 0.2
    }
  };
};

// 截断文本以适应单元格（最多两行；超长时在进表前截断并加结尾「...」，宽度按当前字体测量）
const TRUNCATE_ELLIPSIS = '...';

const twoLineWidthFits = (
  doc: jsPDF,
  s: string,
  availableWidth: number,
  singleLineMaxChars: number
): boolean => {
  const firstLine = s.substring(0, Math.min(singleLineMaxChars, s.length));
  if (doc.getTextWidth(firstLine) > availableWidth) {
    return false;
  }
  const secondLine = s.substring(singleLineMaxChars);
  return doc.getTextWidth(secondLine) <= availableWidth;
};

const truncateTextForCell = (
  doc: jsPDF,
  text: string,
  columnWidth: number,
  fontSize: number,
  cellPadding: number,
  fontName: string,
  maxLines: number = 2
): string => {
  if (!text) return '';

  if (maxLines !== 2) {
    // 当前 A3 表仅使用两行模型；其它行数退化为整段宽度测量（保守截断一行）
    const availableWidth = columnWidth - cellPadding * 2;
    doc.setFont(fontName);
    doc.setFontSize(fontSize);
    let lo = 0;
    let hi = text.length;
    let best = 0;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const cand = text.substring(0, mid) + (mid < text.length ? TRUNCATE_ELLIPSIS : '');
      if (doc.getTextWidth(cand) <= availableWidth) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best < text.length ? text.substring(0, best) + TRUNCATE_ELLIPSIS : text;
  }

  const availableWidth = columnWidth - cellPadding * 2;

  doc.setFont(fontName);
  doc.setFontSize(fontSize);

  let left = 1;
  let right = text.length;
  let singleLineMaxChars = 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const testText = text.substring(0, mid);
    const testWidth = doc.getTextWidth(testText);

    if (testWidth <= availableWidth) {
      singleLineMaxChars = mid;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  if (text.length <= singleLineMaxChars) {
    return text;
  }

  let truncateLeft = singleLineMaxChars + 1;
  let truncateRight = text.length;
  let finalMaxChars = singleLineMaxChars;

  while (truncateLeft <= truncateRight) {
    const mid = Math.floor((truncateLeft + truncateRight) / 2);
    const testText = text.substring(0, mid);

    const firstLine = testText.substring(0, Math.min(singleLineMaxChars, testText.length));
    const firstLineWidth = doc.getTextWidth(firstLine);

    if (firstLineWidth > availableWidth) {
      truncateRight = mid - 1;
      continue;
    }

    const secondLine = testText.substring(singleLineMaxChars, mid);
    const secondLineWidth = secondLine ? doc.getTextWidth(secondLine) : 0;

    if (secondLineWidth <= availableWidth) {
      finalMaxChars = mid;
      truncateLeft = mid + 1;
    } else {
      truncateRight = mid - 1;
    }
  }

  if (finalMaxChars >= text.length) {
    return text;
  }

  let lo = 0;
  let hi = finalMaxChars;
  let best = 0;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const candidate = text.substring(0, mid) + TRUNCATE_ELLIPSIS;
    if (twoLineWidthFits(doc, candidate, availableWidth, singleLineMaxChars)) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  return text.substring(0, best) + TRUNCATE_ELLIPSIS;
};

// 导出分场表A3 PDF
export const exportScenesTableA3 = async (
  doc: jsPDF, 
  scenes: SceneWithUserNames[], 
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage,
  secondaryLanguage: SupportedLanguage | undefined,
  sort: PdfScenesExportSort = DEFAULT_PDF_SCENES_EXPORT_SORT,
  optionName?: string,
  breakdownId?: string,
  getElementCache?: (breakdownId: string) => ElementDataCache | null,
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null
): Promise<jsPDF> => {
  // 初始化页面和字体
  const { doc: initializedDoc, fontName } = await initializeA3Page(doc, language);
  doc = initializedDoc;
  
  // 从缓存获取 elements 和 elementRelations
  const elementsBySceneId = new Map<string, ElementInfo[]>();
  
  if (breakdownId && getElementCache && getElementRelationCache) {
    const elementCache = getElementCache(breakdownId);
    const elementRelationCache = getElementRelationCache(breakdownId);
    
    const allElements = elementCache?.elements || [];
    const allRelations = elementRelationCache?.elementRelations || [];
    
    // 创建 elementID 到 ElementInfo 的映射
    const elementMap = new Map<string, ElementInfo>();
    allElements.forEach(item => {
      elementMap.set(item.elementID, item);
    });
    
    // 根据关系，将 elements 按 sceneID 分组，并合并关系中的数量信息
    allRelations.forEach(relation => {
      const element = elementMap.get(relation.elementID);
      if (element) {
        // 创建一个副本，并添加关系中的数量信息
        const enrichedItem = {
          ...element,
          elementQtyInScene: relation.elementQtyInScene || 1
        };
        const existing = elementsBySceneId.get(relation.sceneID) || [];
        elementsBySceneId.set(relation.sceneID, [...existing, enrichedItem]);
      }
    });
  }
  
  const parseCache = new Map<string, SceneCodeParts | null>();
  const sortedScenes = sortScenesForPdfExport(
    scenes.filter((scene) => !isSceneRowEmpty(scene)),
    sort,
    parseCache
  );
  
  // 获取主要角色数据（从缓存）
  const castTextBySceneId = getCastTextByScenes(sortedScenes, elementsBySceneId);
  
  // 获取表格配置
  const tableConfig = getTableConfig(doc, fontName, language);
  
  // 设置字体和大小用于文本宽度测量
  doc.setFont(fontName);
  doc.setFontSize(STYLES.TABLE_FONT_SIZE);
  
  // 构建表格数据，并对每列文本进行截断（使用实际文本宽度测量）
  const data = sortedScenes.map(scene => [
    truncateTextForCell(doc, scene.sceneCode || '', tableConfig.columnWidths[0], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
    truncateTextForCell(doc, formatInteriorExteriorForExport(language, scene.sceneInteriorExterior, secondaryLanguage), tableConfig.columnWidths[1], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
    truncateTextForCell(doc, formatDayNightForExport(language, scene.sceneDayNight, secondaryLanguage), tableConfig.columnWidths[2], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
    truncateTextForCell(doc, scene.sceneSet || '', tableConfig.columnWidths[3], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
    truncateTextForCell(doc, scene.scenePlot || '', tableConfig.columnWidths[4], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
    truncateTextForCell(doc, scene.sceneLocation || '', tableConfig.columnWidths[5], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
    truncateTextForCell(doc, castTextBySceneId.get(scene.sceneID) || '', tableConfig.columnWidths[6], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2)
  ]);
  
  // 第一页先绘制 header
  addTitleHeader(doc, breakdownInfo, language, optionName || t('export.excelBreakdownBannerLabel', { lng: language }), 1);
  
  // 生成表格
  doc.setFontSize(STYLES.TABLE_FONT_SIZE);
  setFont(doc, language, 'normal');
  
  autoTable(doc, {
    // head: [tableConfig.headers], // 注释掉表头导出，但不删除代码
    body: data,
    startY: STYLES.TABLE_START_Y, // 使用统一样式常量，确保每页表格位置一致
    theme: 'grid',
    /** 固定行高：禁止数据行跨页拆成两段（整行移到下一页） */
    rowPageBreak: 'avoid',
    margin: { 
      left: tableConfig.margin, 
      right: tableConfig.margin,
      top: STYLES.TABLE_START_Y, // 设置顶部边距，确保后续页面表格从相同位置开始
      bottom: tableConfig.margin // 设置底部边距，确保表格不会与底部边距重叠
    },
    tableWidth: tableConfig.tableWidth,
    showHead: 'firstPage', // 只在第一页显示表头
    styles: tableConfig.baseStyles,
    headStyles: tableConfig.headStyles,
    // alternateRowStyles: {
    //   fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 间隔色 - 已注释
    // },
    columnStyles: {
      0: { cellWidth: tableConfig.columnWidths[0] },
      1: { cellWidth: tableConfig.columnWidths[1] },
      2: { cellWidth: tableConfig.columnWidths[2] },
      3: { cellWidth: tableConfig.columnWidths[3] },
      4: { cellWidth: tableConfig.columnWidths[4] },
      5: { cellWidth: tableConfig.columnWidths[5] },
      6: { cellWidth: tableConfig.columnWidths[6] }
    },
    didParseCell: function(data: any) {
      // 为所有 body 行的单元格设置固定高度
      if (data.row.section === 'body') {
        // 设置固定高度，确保所有行高度一致
        // 使用 minHeight 和 height 双重设置确保固定高度生效
        data.cell.minHeight = STYLES.ROW_HEIGHT;
        data.cell.height = STYLES.ROW_HEIGHT;
        // 设置行高，限制文本行数
        if (!data.cell.styles) {
          data.cell.styles = {};
        }
        data.cell.styles.lineHeight = (STYLES.ROW_HEIGHT - STYLES.CELL_PADDING * 2) / 2; // 两行文本的行高
        data.cell.styles.valign = 'top'; // 垂直向上对齐
        data.cell.styles.lineColor = [0, 0, 0];
        data.cell.styles.lineWidth = 0.2;
      }
    },
    didDrawPage: function(data: any) {
      // 在每一页绘制 header（第一页已在表格绘制前绘制，这里处理后续页面）
      if (data.pageNumber > 1) {
        const pageNumber = data.pageNumber;
        addTitleHeader(doc, breakdownInfo, language, optionName || t('export.excelBreakdownBannerLabel', { lng: language }), pageNumber);
      }
    }
  });
  
  return doc;
};

// 导出分景表A3 PDF（按拍摄地点分组）
export const exportSetsTableA3 = async (
  doc: jsPDF, 
  scenes: SceneWithUserNames[], 
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage,
  secondaryLanguage: SupportedLanguage | undefined,
  sort: PdfScenesExportSort = DEFAULT_PDF_SCENES_EXPORT_SORT,
  optionName?: string,
  breakdownId?: string,
  getElementCache?: (breakdownId: string) => ElementDataCache | null,
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null
): Promise<jsPDF> => {
  // 初始化页面和字体
  const { doc: initializedDoc, fontName } = await initializeA3Page(doc, language);
  doc = initializedDoc;
  
  // 从缓存获取 elements 和 elementRelations
  const elementsBySceneId = new Map<string, ElementInfo[]>();
  
  if (breakdownId && getElementCache && getElementRelationCache) {
    const elementCache = getElementCache(breakdownId);
    const elementRelationCache = getElementRelationCache(breakdownId);
    
    const allElements = elementCache?.elements || [];
    const allRelations = elementRelationCache?.elementRelations || [];
    
    // 创建 elementID 到 ElementInfo 的映射
    const elementMap = new Map<string, ElementInfo>();
    allElements.forEach(item => {
      elementMap.set(item.elementID, item);
    });
    
    // 根据关系，将 elements 按 sceneID 分组，并合并关系中的数量信息
    allRelations.forEach(relation => {
      const element = elementMap.get(relation.elementID);
      if (element) {
        // 创建一个副本，并添加关系中的数量信息
        const enrichedItem = {
          ...element,
          elementQtyInScene: relation.elementQtyInScene || 1
        };
        const existing = elementsBySceneId.get(relation.sceneID) || [];
        elementsBySceneId.set(relation.sceneID, [...existing, enrichedItem]);
      }
    });
  }
  
  // 准备表格数据：按拍摄地点分组
  const parseCache = new Map<string, SceneCodeParts | null>();
  const validScenes = scenes.filter(scene => !isSceneRowEmpty(scene)); // 过滤所有字段都为空的行
  
  // 按拍摄地点分组
  const scenesByLocation = new Map<string, SceneWithUserNames[]>();
  validScenes.forEach(scene => {
    const location = (scene.sceneLocation || '').trim() || t('export.locationPending', { lng: language });
    if (!scenesByLocation.has(location)) {
      scenesByLocation.set(location, []);
    }
    scenesByLocation.get(location)!.push(scene);
  });
  
  const sortedLocations = sortSetsGroupedLocations(scenesByLocation, sort, parseCache);
  
  // 获取主要角色数据
  const castTextBySceneId = getCastTextByScenes(validScenes, elementsBySceneId);
  
  // 获取表格配置
  const tableConfig = getTableConfig(doc, fontName, language);
  
  // 第一页先绘制 header
  addTitleHeader(doc, breakdownInfo, language, optionName || t('export.excelSetsBannerLabel', { lng: language }), 1);
  
  // 计算新的列宽（去掉拍摄地点列，将其宽度分配给其他列）
  const pageWidthForTable = doc.internal.pageSize.getWidth();
  const margin = STYLES.PAGE_MARGIN;
  const tableWidth = pageWidthForTable - (margin * 2);
  
  // 新的列宽配置（去掉拍摄地点列）
  const setsTableColumnWidths = {
    0: tableWidth * 0.10,  // 场号
    1: tableWidth * 0.05,  // 内外
    2: tableWidth * 0.05,  // 日夜
    3: tableWidth * 0.24,  // 场景
    4: tableWidth * 0.33, // 剧情
    5: tableWidth * 0.23 // 主要角色
  };
  
  // 设置字体和大小用于文本宽度测量
  doc.setFont(fontName);
  doc.setFontSize(STYLES.TABLE_FONT_SIZE);
  
  // 构建表格数据，包含分组行
  const tableBody: any[] = [];
  sortedLocations.forEach((location) => {
    const groupScenes = scenesByLocation.get(location)!;
    
    // 添加分组标题行（拍摄地点行）- 跨6列（去掉拍摄地点列后）
    // 使用 colSpan 时，只需要提供第一个单元格对象，jspdf-autotable 会自动处理跨列
    // 分组标题行也需要截断，计算总宽度（6列宽度之和）
    const totalGroupWidth = Object.values(setsTableColumnWidths).reduce((sum, width) => sum + width, 0);
    tableBody.push([
      { 
        content: truncateTextForCell(doc, location, totalGroupWidth, STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2), 
        colSpan: 6, 
        styles: { fillColor: hexToRgb(COLORS.LIGHT_GRAY), textColor: COLORS.TEXT_BLACK, fontStyle: 'bold', halign: 'center', valign: 'top' } 
      }
    ]);
    
    // 添加该分组下的所有场次数据（去掉拍摄地点列），并对每列文本进行截断（使用实际文本宽度测量）
    groupScenes.forEach(scene => {
      tableBody.push([
        truncateTextForCell(doc, scene.sceneCode || '', setsTableColumnWidths[0], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
        truncateTextForCell(doc, formatInteriorExteriorForExport(language, scene.sceneInteriorExterior, secondaryLanguage), setsTableColumnWidths[1], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
        truncateTextForCell(doc, formatDayNightForExport(language, scene.sceneDayNight, secondaryLanguage), setsTableColumnWidths[2], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
        truncateTextForCell(doc, scene.sceneSet || '', setsTableColumnWidths[3], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
        truncateTextForCell(doc, scene.scenePlot || '', setsTableColumnWidths[4], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2),
        truncateTextForCell(doc, castTextBySceneId.get(scene.sceneID) || '', setsTableColumnWidths[5], STYLES.TABLE_FONT_SIZE, STYLES.CELL_PADDING, fontName, 2) // 去掉 scene.sceneLocation
      ]);
    });
  });
  
  // 生成表格
  doc.setFontSize(STYLES.TABLE_FONT_SIZE);
  setFont(doc, language, 'normal');
  
  autoTable(doc, {
    // head: [tableConfig.headers], // 注释掉表头导出，但不删除代码
    body: tableBody,
    startY: STYLES.TABLE_START_Y, // 使用统一样式常量，确保每页表格位置一致
    theme: 'grid',
    /** 固定行高：禁止数据行跨页拆成两段（整行移到下一页） */
    rowPageBreak: 'avoid',
    margin: { 
      left: tableConfig.margin, 
      right: tableConfig.margin,
      top: STYLES.TABLE_START_Y, // 设置顶部边距，确保后续页面表格从相同位置开始
      bottom: tableConfig.margin // 设置底部边距，确保表格不会与底部边距重叠
    },
    tableWidth: tableConfig.tableWidth,
    showHead: 'firstPage', // 只在第一页显示表头
    styles: tableConfig.baseStyles,
    headStyles: tableConfig.headStyles,
    // 不设置 alternateRowStyles，因为不需要间隔色
    columnStyles: {
      0: { cellWidth: setsTableColumnWidths[0] }, // 场号
      1: { cellWidth: setsTableColumnWidths[1] }, // 内外
      2: { cellWidth: setsTableColumnWidths[2] }, // 日夜
      3: { cellWidth: setsTableColumnWidths[3] }, // 场景
      4: { cellWidth: setsTableColumnWidths[4] }, // 剧情
      5: { cellWidth: setsTableColumnWidths[5] }  // 主要角色（去掉拍摄地点列）
    },
    didParseCell: function(data: any) {
      // 为所有 body 行的单元格设置固定高度
      if (data.row.section === 'body') {
        // 设置固定高度，确保所有行高度一致
        // 使用 minHeight 和 height 双重设置确保固定高度生效
        data.cell.minHeight = STYLES.ROW_HEIGHT;
        data.cell.height = STYLES.ROW_HEIGHT;
        // 设置行高，限制文本行数
        if (!data.cell.styles) {
          data.cell.styles = {};
        }
        data.cell.styles.lineHeight = (STYLES.ROW_HEIGHT - STYLES.CELL_PADDING * 2) / 2; // 两行文本的行高
        data.cell.styles.valign = 'top'; // 垂直向上对齐
        data.cell.styles.lineColor = [0, 0, 0];
        data.cell.styles.lineWidth = 0.2;
      }
    },
    didDrawPage: function(data: any) {
      // 在每一页绘制 header（第一页已在表格绘制前绘制，这里处理后续页面）
      if (data.pageNumber > 1) {
        const pageNumber = data.pageNumber;
        addTitleHeader(doc, breakdownInfo, language, optionName || t('export.excelSetsBannerLabel', { lng: language }), pageNumber);
      }
    }
  });
  
  return doc;
};

